/**
 * "Next up" — the closes and due dates about to land (§13.3).
 *
 * Sits above the due runway because it answers a different question. The runway
 * is about **money owed**: what has been billed and when it has to be paid. This
 * is about **cycle turnover**: which card is about to close, which is what a
 * purchase decision actually hangs on. A card closing tomorrow and a card that
 * just opened are the same on the runway and opposite here.
 *
 * A close is always a projection, a printed due date never is, and the two are
 * styled differently (§2.7) — dashed and muted versus solid and amber. The strip
 * is deliberately shallow: six events, one line each, and a link to the full page
 * rather than an eighth panel of detail on a dashboard that already has plenty.
 */

import { ArrowRight, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";

import { Panel } from "@/components/ui/primitives";
import type { CycleEvent } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "3 Aug" — built from the ISO parts, never `new Date()` on a bare date string,
 *  which parses as UTC midnight and renders as the previous day in IST. */
function shortDate(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1] ?? ""}`;
}

function whenLabel(offset: number): string {
  if (offset === 0) return "today";
  if (offset === 1) return "tomorrow";
  if (offset < 0) return `${-offset} day${offset === -1 ? "" : "s"} ago`;
  return `in ${offset} days`;
}

export function NextUp({ events, limit = 5 }: { events: CycleEvent[]; limit?: number }) {
  if (events.length === 0) {
    return (
      <Panel className="h-full p-5">
        <Header />
        <p className="mt-3 text-sm text-muted">
          No cycle closes or due dates in the next 30 days. MyDeck works these out from each
          card's statement history.
        </p>
      </Panel>
    );
  }

  return (
    <Panel className="h-full p-5">
      <Header />
      <ul className="mt-3 space-y-1">
        {events.slice(0, limit).map((e, i) => (
          <li
            key={`${e.card_id}-${e.kind}-${e.on}`}
            className="animate-rise"
            style={{ animationDelay: `${350 + i * 60}ms` }}
          >
            <Link
              to={`/cards/${e.card_id}`}
              className="group -mx-2 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-raised"
            >
              <span
                aria-hidden
                className={cn(
                  "h-[26px] w-[40px] shrink-0 rounded-[5px] shadow-[inset_0_1px_0_rgb(255_255_255/0.25)] transition-transform duration-500 ease-spring group-hover:-translate-y-0.5 group-hover:-rotate-3",
                  e.kind === "closes" && "opacity-60",
                )}
                style={{
                  backgroundImage: e.theme
                    ? `linear-gradient(140deg, ${e.theme.gradient_from}, ${e.theme.gradient_to})`
                    : "linear-gradient(140deg, #3a3a40, #26262a)",
                }}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block text-[11.5px]",
                    e.kind === "due" && e.day_offset <= 1 ? "text-warn" : "text-faint",
                  )}
                  title={
                    e.projected
                      ? "Projected from this card's statement history — the issuer hasn't printed this date."
                      : "Printed on the statement."
                  }
                >
                  {e.kind === "due" ? "Bill due" : "Cycle closes"} · {whenLabel(e.day_offset)}
                  {e.projected && <span className="ml-1.5 provisional-chip">Est.</span>}
                </span>
                <span className="block truncate text-sm font-medium text-ink">
                  {e.nickname ?? `•••• ${e.last4}`}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-medium tabular-nums text-ink">
                  {e.amount !== null ? formatMoney(e.amount) : ""}
                </span>
                <span className="block font-mono text-[10.5px] text-faint">{shortDate(e.on)}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Header() {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="label-micro flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5" aria-hidden />
        Next up
      </span>
      <Link
        to="/cycles"
        className="inline-flex items-center gap-1 text-xs text-muted transition-colors hover:text-ink"
      >
        Cycles
        <ArrowRight className="h-3 w-3" aria-hidden />
      </Link>
    </div>
  );
}
